import { Capacitor } from "@capacitor/core";
import { useLingui } from "@lingui/solid/macro";

import { Client } from "stoat.js";

import { useModals } from "@revolt/modal";
import { useState } from "@revolt/state";
import { useSnackbar } from "@revolt/ui";

import { IS_DEV, useClient } from ".";
import {
  isNativePush,
  nativePushOperation,
  registerNativePush,
  unregisterNativePush,
} from "./NativePush";

export function useNotifications() {
  const { settings } = useState();
  const { t } = useLingui();
  const getClient = useClient();
  const snackbar = useSnackbar();
  const { showError } = useModals();

  const supportsNotification = !isNativePush() && "Notification" in window;

  const onDeny = async (showModal?: boolean) => {
    settings.resetNotificationsState("denied");
    if (showModal) {
      showError(
        t`Failed to enable notifications. Stoat does not have notification permission.`,
      );
    }
    await killServiceWorkerSubscription(getClient());
  };

  const notificationStateMismatch = (): boolean => {
    const areNotificationsAllowed =
      settings.desktopNotificationsState === "allowed" ||
      settings.pushNotificationsState === "allowed";

    const notificationPermissionGranted =
      !supportsNotification || Notification.permission === "granted";

    return areNotificationsAllowed && !notificationPermissionGranted;
  };

  const initNotifications = async () => {
    if (isNativePush()) {
      settings.desktopNotificationsState = "unsupported";
      if (settings.pushNotificationsState !== "denied")
        await enablePushSubscription();
      return;
    }
    if (
      settings.desktopNotificationsState === "default" ||
      notificationStateMismatch()
    ) {
      // We do this before permission checking because the constructor will still work fine if we don't have permission.
      if (supportsNotification) {
        try {
          const noti = new Notification(
            "This is what notifications will look like. You shouldn't see this for long.",
            { silent: true },
          );
          // Close the notification just after showing
          // On very slow desktop systems, 100 ms just isn't long enough. Skill issue I guess.
          noti.addEventListener("show", () =>
            setTimeout(() => noti.close(), 100),
          );
        } catch {
          // An error means not supported.
          settings.desktopNotificationsState = "unsupported";
        }
      } else {
        settings.desktopNotificationsState = "unsupported";
      }

      if (supportsNotification) {
        if ((await Notification.requestPermission()) === "granted") {
          settings.desktopNotificationsState = "allowed";
          await enablePushSubscription();
        } else {
          await onDeny();
        }
      } else {
        await enablePushSubscription();
      }
    }
  };

  const toggleNotificationPermission = async (modalOnDeny?: boolean) => {
    if (isNativePush()) return togglePushPermission(modalOnDeny);
    if (settings.desktopNotificationsState !== "allowed") {
      if ((await Notification.requestPermission()) === "granted") {
        settings.desktopNotificationsState = "allowed";
      } else {
        await onDeny(modalOnDeny);
      }
    } else {
      settings.desktopNotificationsState = "denied";
    }
  };

  const enablePushSubscription = async (requestPermission = true) => {
    const previousState = settings.pushNotificationsState;
    try {
      const enabled = isNativePush()
        ? await setUpNativeSubscription(
            getClient(),
            requestPermission,
            getClient,
          )
        : (await setUpServiceWorkerSubscription(getClient()), true);
      settings.pushNotificationsState = enabled ? "allowed" : "denied";
    } catch (e) {
      console.error(e);
      snackbar.show({
        message: t`Failed to enable push notifications. Please try again later.`,
      });
      settings.pushNotificationsState =
        previousState === "allowed" ? "allowed" : "default";
    }
  };

  const togglePushPermission = async (modalOnDeny?: boolean) => {
    if (settings.pushNotificationsState !== "allowed") {
      if (supportsNotification) {
        if ((await Notification.requestPermission()) === "granted") {
          await enablePushSubscription();
        } else {
          await onDeny(modalOnDeny);
        }
      } else {
        // On safari mobile, just enable push notifications.
        await enablePushSubscription();
      }
    } else {
      try {
        await killServiceWorkerSubscription(getClient());
        settings.pushNotificationsState = "denied";
      } catch (error) {
        showError(error);
      }
    }
  };

  return {
    toggleNotificationPermission,
    togglePushPermission,
    initNotifications,
    refreshNativePush: () => {
      if (isNativePush() && settings.pushNotificationsState === "allowed") {
        return enablePushSubscription(false);
      }
    },
  };
}

async function setUpNativeSubscription(
  client: Client,
  requestPermission: boolean,
  currentClient: () => Client,
) {
  return nativePushOperation(async () => {
    if (currentClient() !== client) return false;
    const apiUrl = import.meta.env.VITE_NATIVE_API_URL;
    if (
      !client.configured() ||
      !client.user ||
      !apiUrl ||
      client.options.baseURL?.replace(/\/$/, "") !== apiUrl
    ) {
      throw new Error(
        "Native push requires the configured private instance and a signed-in account.",
      );
    }
    return registerNativePush(
      Capacitor.getPlatform(),
      (subscription) => {
        if (currentClient() !== client)
          throw new Error(
            "The signed-in account changed during push registration.",
          );
        return client.api.post("/push/subscribe", subscription);
      },
      requestPermission,
    );
  });
}

async function setUpServiceWorkerSubscription(client: Client) {
  if (IS_DEV) {
    console.log("Skipping push worker in dev.");
    return;
  }

  if (!client.configured() || !client.configuration) {
    throw "Client not configured";
  }

  const registration = await navigator.serviceWorker.getRegistration(
    import.meta.env.BASE_URL ?? undefined,
  );
  if (!registration) {
    throw "Failed to get service worker";
  }

  const subscription =
    (await registration.pushManager.getSubscription()) ||
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: client.configuration!.vapid,
    }));

  await client.api.post("/push/subscribe", {
    endpoint: subscription.endpoint,
    p256dh: arrayBufferToBase64URL(
      subscription.getKey("p256dh") || new ArrayBuffer(),
    ),
    auth: arrayBufferToBase64URL(
      subscription.getKey("auth") || new ArrayBuffer(),
    ),
  });
}

function arrayBufferToBase64URL(buffer: ArrayBuffer): string {
  const intArray = new Uint8Array(buffer);
  return intArray.toBase64({ alphabet: "base64url" });
}

/** Exported for the client controller. Don't use this unless you have to. */
export async function killServiceWorkerSubscription(
  client: Client,
  loggingOut?: boolean,
) {
  if (isNativePush()) {
    return nativePushOperation(() =>
      unregisterNativePush(() => client.api.post("/push/unsubscribe")),
    );
  }
  if (IS_DEV) {
    console.log("Skipping killing push worker in dev.");
    return;
  }

  const registration = await navigator.serviceWorker.getRegistration(
    import.meta.env.BASE_URL ?? undefined,
  );
  if (!registration) return;
  const subscription = await registration.pushManager.getSubscription();
  if (await subscription?.unsubscribe()) {
    if (!loggingOut) await client.api.post("/push/unsubscribe");
  }
}
